import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { api, errMsg, fileUrl, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useQuote } from "@/lib/useQuote";
import { CartTotals, DiscountCodeInput } from "@/components/store/CartLines";
import { Swatch } from "@/lib/colors";
import { normalizeUsCountry, normalizeUsState } from "@/lib/usAddress";

const Field = ({
  label,
  id,
  form,
  set,
  required = true,
  type = "text",
  half = false,
  auto,
  error = "",
  onBlur,
}) => (
  <label className={`block ${half ? "" : "sm:col-span-2"}`}>
    <span className="eyebrow block mb-2">
      {label}
      {!required && " (optional)"}
    </span>
    <input
      id={id}
      type={type}
      required={required}
      autoComplete={auto}
      value={form[id]}
      onChange={(e) => set({ ...form, [id]: e.target.value })}
      onBlur={onBlur}
      aria-invalid={Boolean(error)}
      className="store-input"
      data-testid={`checkout-${id.replace(/_/g, "-")}-input`}
    />
    {error && (
      <span
        className="mt-1.5 block text-xs text-[#ff6b6b]"
        data-testid={`checkout-${id.replace(/_/g, "-")}-error`}
      >
        {error}
      </span>
    )}
  </label>
);

export default function Checkout() {
  const { items, code } = useCart();
  const [params] = useSearchParams();
  const [form, set] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "United States",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [options, setOptions] = useState({ paypal: false });
  const [shippingServiceKey, setShippingServiceKey] = useState("");
  const [showAllShipping, setShowAllShipping] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    api
      .get("/checkout/options")
      .then((r) => setOptions(r.data))
      .catch(() => {});
  }, []);
  const normalizedState = normalizeUsState(form.state);
  const normalizedCountry = normalizeUsCountry(form.country);
  const addressErrors = {};
  if (!form.line1.trim()) addressErrors.line1 = "Enter your street address.";
  if (!form.city.trim()) addressErrors.city = "Enter your city.";
  if (!normalizedState) addressErrors.state = "Enter a valid U.S. state.";
  if (!/^\d{5}(?:-?\d{4})?$/.test(form.postal_code.trim())) {
    addressErrors.postal_code = "Enter a valid U.S. ZIP code.";
  }
  if (!normalizedCountry) addressErrors.country = "Shipping is currently available within the U.S.";
  const addressReady = Object.keys(addressErrors).length === 0;
  const shippingAddress = addressReady
    ? {
        line1: form.line1.trim(),
        line2: form.line2,
        city: form.city.trim(),
        state: normalizedState,
        postal_code: form.postal_code.trim(),
        country: normalizedCountry,
      }
    : null;
  const {
    quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuote(
    items,
    code,
    form.email,
    shippingAddress,
    shippingServiceKey,
    addressReady,
  );

  const fieldError = (field) =>
    submitted || touched[field] ? addressErrors[field] || "" : "";
  const markTouched = (field) =>
    setTouched((current) => ({ ...current, [field]: true }));
  const shippingUnavailable = quote?.shipping_details?.source === "fallback";
  const shippingOptions = [...(quote?.shipping_details?.options || [])].sort(
    (first, second) => Number(first.amount) - Number(second.amount),
  );
  const standardRate = shippingOptions[0];
  const expressRate = shippingOptions.find(
    (rate) =>
      rate.service_key !== standardRate?.service_key &&
      (/(express|priority|next|2nd|overnight)/i.test(rate.service) ||
        (rate.estimated_days &&
          standardRate?.estimated_days &&
          rate.estimated_days < standardRate.estimated_days)),
  );
  const preferredRates = [standardRate, expressRate].filter(Boolean);
  const freeShippingCredit =
    quote?.discount?.type === "free_shipping"
      ? Number(quote.discount.shipping_off || 0)
      : 0;
  const ratePromotionLabel = (rate) => {
    if (!freeShippingCredit) return money(rate.amount);
    const difference = Math.max(Number(rate.amount) - freeShippingCredit, 0);
    return difference ? `${money(difference)} extra` : "Free";
  };
  const visibleShippingRates = showAllShipping
    ? shippingOptions
    : shippingOptions.filter((rate) =>
        preferredRates.some((preferred) => preferred.service_key === rate.service_key),
      );
  const paymentButtonLabel = loading
    ? "Redirecting…"
    : !addressReady
      ? "Complete delivery address"
      : shippingUnavailable
        ? "Live shipping unavailable"
        : quoteLoading
          ? "Calculating total…"
          : !quote?.tax_ready
            ? "Tax unavailable"
            : quoteError
              ? "Shipping unavailable — check address"
              : "Pay with PayPal";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!addressReady) {
      setError("Complete the delivery address to calculate your final total.");
      return;
    }
    if (!e.currentTarget.reportValidity()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/checkout/paypal", {
        items: items.map((i) => ({
          product_id: i.product_id,
          color: i.color,
          quantity: i.quantity,
        })),
        customer: {
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
        },
        shipping_address: shippingAddress,
        origin_url: window.location.origin,
        code: code && !quote?.code_error ? code : "",
        shipping_service_key: shippingServiceKey,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(errMsg(err));
      setLoading(false);
    }
  };

  if (!items.length)
    return (
      <div
        className="max-w-3xl mx-auto px-5 py-40 text-center"
        data-testid="checkout-empty"
      >
        <h1 className="font-display text-3xl uppercase font-bold">
          Your bag is empty
        </h1>
        <Link to="/shop" className="btn-store mt-8">
          Shop the drop
        </Link>
      </div>
    );

  return (
    <div
      className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14"
      data-testid="checkout-page"
    >
      <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter uppercase text-white">
        Checkout
      </h1>
      {params.get("cancelled") && (
        <div
          className="mt-8 border border-white/15 px-4 py-3 text-sm text-zinc-300"
          data-testid="checkout-cancelled-banner"
        >
          Payment was cancelled. Your bag is still here whenever you're ready.
        </div>
      )}
      <form
        onSubmit={submit}
        className="mt-9 grid lg:grid-cols-[1.35fr_0.9fr] gap-10 lg:gap-16"
      >
        <div className="space-y-10">
          <section>
            <h2 className="font-display text-sm tracking-[0.25em] uppercase text-white mb-6">
              Contact
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Email"
                id="email"
                type="email"
                auto="email"
                form={form}
                set={set}
              />
              <Field
                label="First name"
                id="first_name"
                auto="given-name"
                half
                form={form}
                set={set}
              />
              <Field
                label="Last name"
                id="last_name"
                auto="family-name"
                half
                form={form}
                set={set}
              />
              <Field
                label="Phone"
                id="phone"
                type="tel"
                auto="tel"
                required={false}
                form={form}
                set={set}
              />
            </div>
          </section>
          <section>
            <h2 className="font-display text-sm tracking-[0.25em] uppercase text-white mb-6">
              Shipping address
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Address"
                id="line1"
                auto="address-line1"
                form={form}
                set={set}
                onBlur={() => markTouched("line1")}
                error={fieldError("line1")}
              />
              <Field
                label="Apartment, suite"
                id="line2"
                auto="address-line2"
                required={false}
                form={form}
                set={set}
              />
              <Field
                label="City"
                id="city"
                auto="address-level2"
                half
                form={form}
                set={set}
                onBlur={() => markTouched("city")}
                error={fieldError("city")}
              />
              <Field
                label="State"
                id="state"
                auto="address-level1"
                half
                form={form}
                set={set}
                onBlur={() => markTouched("state")}
                error={fieldError("state")}
              />
              <Field
                label="ZIP code"
                id="postal_code"
                auto="postal-code"
                half
                form={form}
                set={set}
                onBlur={() => markTouched("postal_code")}
                error={fieldError("postal_code")}
              />
              <Field
                label="Country"
                id="country"
                auto="country-name"
                half
                form={form}
                set={set}
                onBlur={() => markTouched("country")}
                error={fieldError("country")}
              />
            </div>
            {addressReady && quoteError && (
              <p
                className="mt-4 text-sm text-[#ff6b6b]"
                data-testid="checkout-shipping-address-error"
              >
                {quoteError}
              </p>
            )}
          </section>
        </div>
        <aside
          className="border border-white/10 bg-[#0a0a0c] p-5 sm:p-7 self-start space-y-6 lg:sticky lg:top-6"
          data-testid="checkout-summary"
        >
          <h2 className="font-display text-sm tracking-[0.25em] uppercase text-white">
            Order summary
          </h2>
          <ul className="space-y-4">
            {items.map((i) => {
              const quoted = quote?.items?.find(
                (line) =>
                  line.product_id === i.product_id && line.color === i.color,
              );
              const linePrice = quoted?.price ?? i.price;
              return (
                <li key={i.key} className="flex gap-4 items-center">
                  <div className="relative w-14 h-16 bg-[#141416] flex-shrink-0">
                    {i.photo && (
                      <img
                        src={fileUrl(i.photo)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                    <span className="absolute -top-2 -right-2 bg-zinc-700 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                      {i.quantity}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{i.name}</p>
                    <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-1">
                      <Swatch name={i.color} size={9} />
                      {i.color}
                    </p>
                  </div>
                  <span className="text-sm text-white">
                    {money(linePrice * i.quantity)}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-white/10 pt-5 space-y-5">
            <DiscountCodeInput quote={quote} />
            {shippingOptions.length > 0 && (
              <div
                className="space-y-2"
                data-testid="checkout-live-shipping-options"
              >
                <p className="text-xs uppercase tracking-[0.14em] text-zinc-500 flex justify-between">
                  <span>Delivery method</span>
                  <span>{shippingOptions.length} options</span>
                </p>
                {visibleShippingRates.map((rate) => (
                  <label
                    key={rate.id}
                    className={`flex items-center justify-between gap-3 border p-3 cursor-pointer transition-colors ${
                      quote.shipping_details.selected_service_key === rate.service_key
                        ? "border-white bg-white/[0.04]"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2 text-sm text-zinc-200">
                      <input
                        type="radio"
                        name="shipping-rate"
                        checked={
                          quote.shipping_details.selected_service_key ===
                          rate.service_key
                        }
                        onChange={() => setShippingServiceKey(rate.service_key)}
                        data-testid={`shipping-rate-${rate.service_key}`}
                      />
                      <span className="min-w-0">
                        <span className="block truncate">
                          {rate.carrier} · {rate.service}
                        </span>
                        {rate.estimated_days && (
                          <span className="block text-xs text-zinc-500 mt-0.5">
                            {rate.estimated_days} {rate.estimated_days === 1 ? "day" : "days"}
                          </span>
                        )}
                      </span>
                    </span>
                    <strong
                      className="text-sm"
                      data-testid={`shipping-rate-price-${rate.service_key}`}
                    >
                      {ratePromotionLabel(rate)}
                    </strong>
                  </label>
                ))}
                {freeShippingCredit > 0 && (
                  <p
                    className="text-xs text-zinc-400"
                    data-testid="checkout-free-shipping-explanation"
                  >
                    This promotion covers the cheapest delivery option. Choose a faster service and
                    pay only the difference.
                  </p>
                )}
                {shippingOptions.length > preferredRates.length && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
                    onClick={() => setShowAllShipping((current) => !current)}
                    data-testid="checkout-toggle-shipping-options"
                  >
                    {showAllShipping ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {showAllShipping ? "Show fewer options" : "View all shipping options"}
                  </button>
                )}
              </div>
            )}
            {quote?.shipping_details?.source === "fallback" && (
              <p
                className="text-sm text-[#ff6b6b]"
                data-testid="checkout-shipping-fallback-note"
                role="alert"
              >
                Live shipping could not be calculated. Payment is unavailable. Please review your
                delivery address and try again.
              </p>
            )}
            <CartTotals
              quote={quote}
              quoteError={quoteError}
              loading={quoteLoading}
              awaitingAddress={!addressReady}
            />
          </div>
          {error && (
            <p className="text-sm text-[#ff6b6b]" data-testid="checkout-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={
              !!loading ||
              quoteLoading ||
              !options.paypal ||
              !addressReady ||
              !!quoteError ||
              shippingUnavailable ||
              !quote?.tax_ready
            }
            className="btn-store w-full"
            data-testid="checkout-paypal-button"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {paymentButtonLabel}
          </button>
          <p
            className="text-[11px] text-zinc-500 leading-relaxed"
            data-testid="checkout-paypal-note"
          >
            Secure payment by PayPal. We never see or store your payment
            details.
          </p>
        </aside>
      </form>
    </div>
  );
}
